import React, { useState, useEffect } from 'react';
import { Loader2, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { fetchWithTenant } from '../utils/apiClient';
import configs from '../config';

const NOTIFICATION_SETTINGS = [
  {
    id: 'proposalUpdates',
    label: 'Proposal Updates',
    description: 'Get notified when proposal statuses change.',
  },
  {
    id: 'newMessages',
    label: 'New Messages',
    description: 'Get notified when you receive new messages.',
  },
  {
    id: 'jobAlerts',
    label: 'Job Alerts',
    description: 'Get notified when new jobs matching your preferences are posted.',
  },
  {
    id: 'syncComplete',
    label: 'Sync Complete',
    description: 'Get notified when data sync with Upwork completes.',
  },
];

const DEFAULT_PREFS = {
  proposalUpdates: true,
  newMessages: true,
  jobAlerts: true,
  syncComplete: true,
};

export default function SettingsNotifications() {
  const [settings, setSettings] = useState(DEFAULT_PREFS);
  const [isLoading, setIsLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [testStatus, setTestStatus] = useState(null); // 'loading', 'success', 'error'
  const [testMessage, setTestMessage] = useState('');

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await fetchWithTenant(`${configs.BACKEND_URL}/api/user/notification-preferences`);
        if (response.ok) {
          const data = await response.json();
          setSettings({ ...DEFAULT_PREFS, ...data });
        }
      } catch (err) {
        console.error('Failed to load notification preferences:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPreferences();
  }, []);

  const toggleSetting = async (id) => {
    const newValue = !settings[id];
    setSettings(prev => ({ ...prev, [id]: newValue }));
    setSavingId(id);

    try {
      const response = await fetchWithTenant(`${configs.BACKEND_URL}/api/user/notification-preferences`, {
        method: 'PUT',
        body: JSON.stringify({ [id]: newValue }),
      });
      if (!response.ok) {
        // Revert on failure
        setSettings(prev => ({ ...prev, [id]: !newValue }));
      }
    } catch (err) {
      console.error('Failed to save notification preference:', err);
      setSettings(prev => ({ ...prev, [id]: !newValue }));
    } finally {
      setSavingId(null);
    }
  };

  const sendTestNotification = async () => {
    setTestStatus('loading');
    setTestMessage('');

    try {
      const response = await fetchWithTenant(`${configs.BACKEND_URL}/api/notifications/test`, {
        method: 'POST',
      });

      if (response.ok) {
        setTestStatus('success');
        setTestMessage('Test notification sent! Check your notifications.');
        setTimeout(() => {
          setTestStatus(null);
          setTestMessage('');
        }, 5000);
      } else {
        throw new Error('Failed to send test notification');
      }
    } catch (err) {
      console.error('Failed to send test notification:', err);
      setTestStatus('error');
      setTestMessage(err.message || 'Failed to send test notification. Please try again.');
      setTimeout(() => {
        setTestStatus(null);
        setTestMessage('');
      }, 5000);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-medium text-gray-900 mb-1">Notifications</h2>
      <p className="text-sm text-gray-500 mb-6">Choose which notifications you'd like to receive.</p>

      <div className="space-y-4">
        {NOTIFICATION_SETTINGS.map(({ id, label, description }) => (
          <div key={id} className="flex items-start justify-between gap-4 py-3 border-b border-gray-100 last:border-0">
            <div>
              <p className="text-sm font-medium text-gray-900">{label}</p>
              <p className="text-sm text-gray-500 mt-0.5">{description}</p>
            </div>
            <button
              role="switch"
              aria-checked={settings[id]}
              onClick={() => toggleSetting(id)}
              disabled={savingId === id}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 ${
                settings[id] ? 'bg-primary-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  settings[id] ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
        ))}
      </div>

      {/* Test Notification Section */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <h3 className="text-sm font-medium text-gray-900 mb-2">Test Notifications</h3>
        <p className="text-sm text-gray-500 mb-4">
          Send a test notification to make sure everything is working correctly.
        </p>

        <button
          onClick={sendTestNotification}
          disabled={testStatus === 'loading'}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {testStatus === 'loading' ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Sending...
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              Send Test Notification
            </>
          )}
        </button>

        {/* Status Message */}
        {testMessage && (
          <div className={`mt-3 p-3 rounded-lg flex items-start gap-2 ${
            testStatus === 'success' ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
          }`}>
            {testStatus === 'success' ? (
              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            )}
            <p className={`text-sm ${testStatus === 'success' ? 'text-green-800' : 'text-red-800'}`}>
              {testMessage}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
